import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import { TranslateQuestionnaireContent } from '../../shared/TranslateQuestionnaire/TranslateQuestionnaireContent';

import type { CycleQuestionnaire } from '@/types/cycle-evaluation/CycleQuestionnaire';
import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { LocalizedText } from '@/utils/localization';

import { useGetCycleEvaluation } from '@/api/cycle-evaluation-services/cycle-evaluation';
import { useUpdateCycleQuestionnaire } from '@/api/cycle-evaluation-services/cycle-questionnaire';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { useRevieweesFromAudience } from '@/hooks/cycle-instance/useRevieweesFromAudience';
import { useError } from '@/hooks/utils/useError';
import { RouteName } from '@/navigation/types';
import {
    createTranslateTitleSchema,
    type TranslateTitleFormData,
} from '@/pages/shared/QuestionnaireCreation/validation';
import { showSuccessToast, showWarningToast } from '@/plugins/Toast';
import { validationMessageKey } from '@/utils/validationMessage';

export const TranslateQuestionnairePage = () => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();
    const { handleError } = useError();
    const location = useLocation<{ cycleEvaluationId?: string; step?: string }>();

    const cycleEvaluationId = location.state?.cycleEvaluationId || '';
    const step = location.state?.step || '';

    const { data: cycleEvaluation, isLoading } = useGetCycleEvaluation(cycleEvaluationId);
    const { mutateAsync: updateQuestionnaire, isPending: isSaving } = useUpdateCycleQuestionnaire();

    const reviewees = useRevieweesFromAudience({ cycleEvaluation });
    const previewEvaluee = reviewees[0];

    const questions = cycleEvaluation?.cycleQuestionnaire?.reviewQuestions || [];

    const [targetLanguage, setTargetLanguage] = useState<string>('');

    const translateTitleSchema = useMemo(
        () => createTranslateTitleSchema(targetLanguage),
        [targetLanguage],
    );

    // TODO: The title input is managed as localTitle in TranslateQuestionnaireContent; useForm here is
    // used only as a validation trigger (setValue → trigger → errors). Intentional — more translatable
    // fields are planned. When added, consider extracting this into a shared useTranslatePage hook.
    const {
        watch,
        setValue,
        reset,
        trigger,
        formState: { errors },
    } = useForm<TranslateTitleFormData>({
        resolver: zodResolver(translateTitleSchema),
        defaultValues: { title: {} },
    });

    const formTitle = watch('title');

    const onTargetLanguageChange = useCallback((language: string) => {
        setTargetLanguage(language);
    }, []);

    useEffect(() => {
        if (targetLanguage) {
            reset({ title: formTitle });
        }
    }, [targetLanguage]);

    const onSave = async (updatedQuestions: ReviewQuestion[], updatedTitle?: LocalizedText) => {
        if (!cycleEvaluation?.cycleQuestionnaire) return;

        const titleToSave = updatedTitle ?? cycleEvaluation.cycleQuestionnaire.title;
        setValue('title', titleToSave);

        const isValid = await trigger('title');

        if (!isValid) {
            const errorMessage = errors.title?.title?.message;
            if (errorMessage) {
                return showWarningToast({ message: t(validationMessageKey(errorMessage)) });
            }
        }

        try {
            const updatedQuestionnaire: CycleQuestionnaire = {
                id: cycleEvaluation.cycleQuestionnaire.id,
                title: titleToSave,
                cycleEvaluationId: cycleEvaluation.id,
                reviewTemplateId: cycleEvaluation.cycleQuestionnaire.reviewTemplateId,
                reviewQuestions: updatedQuestions,
            };

            await updateQuestionnaire(updatedQuestionnaire);
            showSuccessToast({
                message: t('templateManagement.templateTranslation.message.saveSuccess'),
            });
        } catch (error) {
            handleError({ error });
        }
    };

    const onBack = () => {
        navigate({
            path: `${RouteName.CycleEvaluationForm}/${step}/${cycleEvaluationId}`,
        });
    };

    return (
        <TranslateQuestionnaireContent
            questions={questions}
            isLoading={isLoading}
            isSaving={isSaving}
            onSave={onSave}
            onBack={onBack}
            backLabel={t('common.backToQuestionnaire')}
            title={t('templateManagement.questionnaireTranslation.title')}
            subtitle={t('templateManagement.questionnaireTranslation.subtitle')}
            evaluationTitle={cycleEvaluation?.cycleQuestionnaire?.title}
            titleError={errors.title?.title?.message as string | undefined}
            onTargetLanguageChange={onTargetLanguageChange}
            previewEvaluee={previewEvaluee}
        />
    );
};
